/**
 * Messages Service
 *
 * API client for message template management.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Provenance + versioning metadata for a template row.
 *
 * Mirrors the backend schema added by migration 0029. Used by the
 * Messages list to render badges (system default / customized / update
 * available / duplicate) and to decide which actions are available
 * ("Restore to system default" is only valid on `source='system_default'`).
 */
export type MessageTemplateSource = 'system_default' | 'custom' | 'duplicate';

export interface MessageTemplate {
  id: string;
  name: string;
  category: string;
  channels: string[];

  // Email
  subject?: string;
  body: string;

  // Per-channel bodies (added with migration 0029)
  smsBody?: string | null;
  whatsappBody?: string | null;
  pushTitle?: string | null;
  pushBody?: string | null;

  isActive: boolean;
  availableTags?: string[];
  stats?: {
    sent: number;
    delivered: number;
    opened: number;
  };
  createdBy?: string;
  createdAt: string;
  updatedAt: string;

  // Source + customization tracking (added with migration 0029)
  source: MessageTemplateSource;
  /** Stable code for system_default rows — e.g. 'vetting.invite'. null for custom/duplicate. */
  sourceCode?: string | null;
  /** Version of the seed at insert / last auto-upgrade. Semver-ish. null for custom/duplicate. */
  sourceVersion?: string | null;
  /** True once the admin has edited a system_default row — guards against auto-upgrade overwrites. */
  customized: boolean;
}

export interface CreateMessageTemplateInput {
  name: string;
  category?: string;
  channels?: string[];
  subject?: string;
  body: string;
  smsBody?: string;
  whatsappBody?: string;
  pushTitle?: string;
  pushBody?: string;
  availableTags?: string[];
}

export interface UpdateMessageTemplateInput {
  name?: string;
  category?: string;
  channels?: string[];
  subject?: string;
  body?: string;
  smsBody?: string | null;
  whatsappBody?: string | null;
  pushTitle?: string | null;
  pushBody?: string | null;
  isActive?: boolean;
  availableTags?: string[];
}

export interface TestSendResult {
  sent: true;
  to: string;
}

/**
 * DTO for the preview endpoint — a DRAFT template body the editor is
 * about to save. The server substitutes sample data + renders the full
 * email shell; used by the editor's live-preview pane.
 */
export interface PreviewMessageTemplateInput {
  subject?: string | null;
  body: string;
  availableTags?: string[];
  /**
   * Optional override for the sample-data tag rendering. When the
   * caller is sending to a specific recipient (e.g. the
   * SendToTalent dialog), pass that recipient's data here so the
   * preview reads "Hi José" instead of the templated "Hi Maria".
   * The Template Editor omits this — it WANTS sample data for
   * canonical copy authoring.
   */
  previewContext?: {
    talent?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    };
  };
}

export interface PreviewMessageTemplateResult {
  /** Rendered subject with sample-data substitutions applied. */
  subject: string;
  /** Full HTML email document — drop into an iframe's srcdoc to render. */
  html: string;
  /** Plain-text alternative — useful for the "plain-text view" toggle. */
  text: string;
}

/**
 * Aggregated analytics for the Messages dashboard.
 *
 * Counts are computed from `message_sends` rollups on the backend
 * (see `apps/platform-api/src/modules/business/business.service.ts`
 * `getMessageAnalytics`). NOT from the per-template `stats` JSONB
 * (which is deprecated and will stay empty for message-center sends).
 */
/**
 * One channel's funnel. (W8.2 · F50 P3 · H17)
 *
 * The server always grouped by channel and returned the counts; the dashboard
 * then rendered the GLOBAL rate for email and a literal `0%` for everything
 * else, deriving a metric in a component from a breakdown it had been handed.
 * These are the real per-channel numbers.
 */
export interface MessageChannelStats {
  /** `email` | `push` | `sms` | `whatsapp` | `in_app`. */
  channel: string;
  /** Every row on this channel, suppressed ones included. */
  total: number;
  /** Rows we actually tried to send — `total` minus the suppressed ones. */
  attempted: number;
  /** Withheld on the recipient's own instruction. Not a delivery attempt. */
  suppressed: number;
  failed: number;
  /** Rows carrying a `delivered_at` INSTANT — never a `delivered` status. */
  delivered: number;
  /** Rows carrying an `opened_at` instant. */
  opened: number;
  /** delivered / attempted — 0..1, 2 decimals. */
  deliveryRate: number;
  /** opened / DELIVERED — 0..1, 2 decimals. Mail that never arrived cannot be
   *  opened, so counting it here would measure deliverability twice. */
  openRate: number;
}

export interface MessageAnalytics {
  /** Rows we tried to send — suppressed and test sends excluded. */
  total: number;
  /** Withheld on the recipient's own instruction, across every channel. */
  suppressed: number;
  /**
   * Rows grouped by their CURRENT status: { queued, sent, delivered, opened,
   * failed, suppressed }.
   *
   * A census of where rows sit, and deliberately not the source of any rate.
   * `status` is one last-write-wins column and the Postmark webhook writes
   * `opened` over `delivered`, so `byStatus.delivered` FALLS as recipients read
   * their mail. Read `delivered` / `deliveryRate` below instead — they count
   * the ledger's separate instants, which are monotonic.
   */
  byStatus: Record<string, number>;
  /** Total rows per channel: { email, whatsapp, sms, push }. */
  byChannel: Record<string, number>;
  /** The same grouping with its funnel, busiest channel first. */
  byChannelBreakdown: MessageChannelStats[];
  /** Rows carrying a `delivered_at` instant, across every channel. */
  delivered: number;
  /** Rows carrying an `opened_at` instant. */
  opened: number;
  /** delivered / attempted — 0..1. Rounded to 2 decimals. */
  deliveryRate: number;
  /** opened / delivered — 0..1. Rounded to 2 decimals. */
  openRate: number;
  /** Daily counts for the last 30 days (date string + count). */
  dailySends: Array<{ date: string; count: number }>;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class MessagesService {
  constructor(private readonly client: TonnieClient) {}

  async getTemplates(): Promise<MessageTemplate[]> {
    // Backend returns `{ templates, total }`. Unwrap so callers see a flat
    // array — simpler for React Query + direct `.map()` rendering. If the
    // backend ever moves to a bare array (legacy), we still support it.
    const response = await this.client.get<
      MessageTemplate[] | { templates: MessageTemplate[]; total: number }
    >(API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES);
    if (Array.isArray(response)) return response;
    return response?.templates ?? [];
  }

  async getTemplate(id: string): Promise<MessageTemplate> {
    return this.client.get<MessageTemplate>(`${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/${id}`);
  }

  async createTemplate(input: CreateMessageTemplateInput): Promise<MessageTemplate> {
    return this.client.post<MessageTemplate>(API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES, input);
  }

  async updateTemplate(id: string, input: UpdateMessageTemplateInput): Promise<MessageTemplate> {
    return this.client.patch<MessageTemplate>(
      `${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/${id}`,
      input
    );
  }

  async deleteTemplate(id: string): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(
      `${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/${id}`
    );
  }

  /**
   * Clone a template — the copy is `source='duplicate'`, untouchable by
   * the bootstrap service. Useful to hold variants (e.g. "invite —
   * fintech voice") alongside the original.
   */
  async duplicateTemplate(id: string): Promise<MessageTemplate> {
    return this.client.post<MessageTemplate>(
      `${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/${id}/duplicate`
    );
  }

  /**
   * Reset a `system_default` row back to the canonical seeded contents.
   * Only valid on system_default rows; backend returns 403 otherwise.
   * Clears the `customized` flag so future auto-upgrades apply.
   */
  async restoreTemplate(id: string): Promise<{ success: boolean }> {
    return this.client.post<{ success: boolean }>(
      `${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/${id}/restore`
    );
  }

  /**
   * Queue a test email to the admin's own address with sample data so
   * they can preview rendered output in a real inbox before sending to
   * real recipients. Email channel only for R1.
   */
  async testSendTemplate(id: string): Promise<TestSendResult> {
    return this.client.post<TestSendResult>(
      `${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/${id}/test-send`
    );
  }

  /**
   * Render a draft template body through the full email shell with
   * sample data. No persistence, no Postmark call — used by the
   * editor's live-preview pane so admins see exactly what the email
   * will look like before they save.
   */
  async previewTemplate(input: PreviewMessageTemplateInput): Promise<PreviewMessageTemplateResult> {
    return this.client.post<PreviewMessageTemplateResult>(
      `${API_ENDPOINTS.BUSINESS.MESSAGES_TEMPLATES}/preview`,
      input
    );
  }

  async getAnalytics(): Promise<MessageAnalytics> {
    return this.client.get<MessageAnalytics>(API_ENDPOINTS.BUSINESS.MESSAGES_ANALYTICS);
  }

  async sendMessage(data: SendMessageTemplateInput): Promise<SendMessageResult> {
    // Was hardcoded to '/business/messages/send' (missing '/api'),
    // which 404'd at `http://host:4000/business/messages/send`.
    // Aligned with the rest of the file via the shared endpoint
    // constant so we never drift again.
    return this.client.post<SendMessageResult>(API_ENDPOINTS.BUSINESS.MESSAGES_SEND, data);
  }
}

/**
 * Payload for `POST /api/business/messages/send`.
 *
 * Must mirror `sendMessageSchema` in
 * `apps/platform-api/src/modules/business/business.controller.ts`. Keeping
 * these two in sync prevents 400s at runtime — a mismatch here is a lint
 * for the backend contract.
 */
export interface SendMessageTemplateInput {
  /** Required when the recipients need template rendering (subject/body). */
  templateId?: string;
  /** Convenience single-recipient form — backend accepts talentId or talentIds. */
  talentId?: string;
  /** Bulk-recipient form. When provided, backend sends one row per id. */
  talentIds?: string[];
  /**
   * H20/BW-2 — message every active member of a cohort. Expanded server-side
   * into `talentIds` (deduped against any explicit ids), so the send goes
   * through the same recipient consent gate and suppression accounting as a
   * single-recipient send. Capped server-side; an oversized cohort is REFUSED,
   * not truncated — a partial broadcast whose boundary the operator cannot see
   * is worse than none.
   */
  cohortId?: string;
  /**
   * Delivery channel. R1 email center only ships "email" end-to-end; the
   * other values are accepted by the backend but remain enqueue-only until
   * ENABLE_EXTENDED_MESSAGE_CHANNELS is flipped on.
   */
  channel: 'email' | 'whatsapp' | 'sms' | 'push';
  /** Override the template subject (optional — template subject used otherwise). */
  subject?: string;
  /** Override the template body (optional). */
  body?: string;
  /**
   * Free-form context for tag rendering. Known keys: `actionUrl`,
   * `actionDeadline`. Everything else is passed through to the renderer as
   * `{{<key>}}` substitutions.
   */
  metadata?: Record<string, unknown>;
}

export interface SendMessageResult {
  /**
   * Recipients actually queued for delivery. EXCLUDES `suppressed`, so a
   * single-recipient send that was suppressed reports `queued: 0` — callers
   * must not report success on that.
   */
  queued: number;
  /**
   * Recipients we deliberately did not send to, because their notification
   * preferences withheld consent for this channel/category. Audited as
   * `message_sends.status = 'suppressed'`; not a failure, and not a delivery.
   */
  suppressed: number;
  sends: Array<{ id: string; talentId?: string | null; status: string }>;
}

export function createMessagesService(client: TonnieClient): MessagesService {
  return new MessagesService(client);
}
